import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const curriculum = [
  { title: 'The AI Developer Landscape', duration: '25 min', lessons: 3 },
  { title: 'OpenAI API Deep Dive', duration: '52 min', lessons: 6 },
  { title: 'Anthropic Claude API & Tool Use', duration: '40 min', lessons: 5 },
  { title: 'Open-Source Models: Llama, Mistral & More', duration: '48 min', lessons: 5 },
  { title: 'Building Chat Interfaces with Streaming', duration: '38 min', lessons: 4 },
  { title: 'Retrieval-Augmented Generation (RAG)', duration: '55 min', lessons: 6 },
  { title: 'Vector Databases & Embeddings', duration: '42 min', lessons: 5 },
  { title: 'Prompt Management & Optimization at Scale', duration: '35 min', lessons: 4 },
  { title: 'Rate Limiting, Caching & Cost Optimization', duration: '40 min', lessons: 4 },
  { title: 'Testing, Monitoring & Evals for LLM Apps', duration: '48 min', lessons: 5 },
  { title: 'Deploying AI Features to Production', duration: '50 min', lessons: 5 },
  { title: 'Capstone: Full-Stack AI Application', duration: '65 min', lessons: 3 },
];

const skills = [
  'OpenAI API', 'Anthropic API', 'Open-Source LLMs', 'RAG Architecture',
  'Vector Databases', 'Embeddings', 'Prompt Optimization', 'LLM Monitoring',
  'Cost Optimization', 'Production Deployment',
];

export default function AIForDevsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-primary-100 text-primary-800 text-xs">
                  Intermediate
                </span>
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-secondary-100 text-secondary-900 text-xs">
                  AI for Developers
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                AI for Developers: APIs to Production
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Integrate AI into your apps using OpenAI, Anthropic, and open-source models. From your first API call to a production-ready RAG pipeline — this course covers the complete developer workflow for shipping AI features.
              </p>

              <div className="flex flex-wrap items-center gap-5 text-sm text-foreground-600 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  12 hours
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  65 lessons
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  9,200 students
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-primary-400" />
                  4.8 (1,050+ reviews)
                </span>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-3xl font-heading font-extrabold text-foreground-950">$99</span>
                <Link
                  to="/checkout?course=ai-for-devs"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 h-11 px-6 text-sm"
                >
                  Enroll Now
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </div>

            <div className="lg:w-80 w-full shrink-0">
              <div className="rounded-xl overflow-hidden border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20developer%20workspace%20with%20code%20editor%20displaying%20API%20integrations%20on%20screen%2C%20clean%20minimal%20white%20background%2C%20abstract%20tech%20visualization%20with%20circuit%20lines%2C%20soft%20warm%20ambient%20lighting%2C%20editorial%20tech%20photography%20style%2C%20professional%20aesthetic%2C%20subtle%20gradient%20accents%2C%20no%20text%2C%20clean%20composition&width=640&height=400&seq=ai-devs-hero&orientation=landscape"
                  alt="AI for Developers: APIs to Production"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">Course Curriculum</h2>
              <div className="space-y-3">
                {curriculum.map((section, idx) => (
                  <div
                    key={section.title}
                    className="flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-background-100 text-foreground-500 text-xs font-semibold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{section.title}</p>
                        <p className="text-xs text-foreground-500">{section.lessons} lessons</p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{section.duration}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">Skills You Will Gain</h3>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-background-100 text-foreground-700 text-xs"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">This Course Includes</h3>
                <ul className="space-y-2 text-sm text-foreground-600">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    12 hours on-demand video
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    65 downloadable resources
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    1 capstone project
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Certificate of completion
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Lifetime access
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Community & Q&A access
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 bg-primary-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-background-50 mb-2">
            Ship AI Features Confidently
          </h2>
          <p className="text-sm text-primary-100 mb-5 max-w-md mx-auto">
            Join 9,200+ developers. One-time payment, lifetime access.
          </p>
          <Link
            to="/checkout?course=ai-for-devs"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-background-50 text-primary-600 hover:bg-background-100 h-10 px-5 text-sm"
          >
            Enroll for $99
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}